import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { Stack } from '@fluentui/react';
import { useOnEvent } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { useBreakOnFirstRender } from 'common/hooks/useBreakOnFirstRender';
import { useElementSize } from 'common/hooks/useElementSize';
import { useNavigation } from 'common/hooks/useNavigation';
import { useScreen } from 'common/hooks/useScreen';
import { screenReaderOnlyStyles } from 'common/utilities/Accessibility';
import { pageDataService } from 'common/utilities/PageDataService';
import { ScreenSize } from 'common/utilities/ResponsiveBreakpoints';
import { useLocalizedStrings } from 'i18n';
import { dashboardPath } from 'Routes';

import { AvatarButtonRow, IAvatarButtonRowProps } from '../Avatar/AvatarButtonRow';
import { PillButton } from '../Buttons/v8';
import ScrollUpButton from '../Buttons/v8/ScrollUpButton/ScrollUpButton';
import { BackIcon } from '../Icons';
import { Heading1 } from '../Text';

import { layout_wrapper_id } from './Layout';
import { getBackButtonClassName, getClassNames, getPageHeadingClassName, tokens } from './PageWrapper.styles';

export interface IPageWrapperProps {
  /**
   * A unique name for the page where the wrapper is rendered. Used for telemetry purpose
   */
  pageName: string;

  /**
   * The page title
   */
  title: string;

  /**
   * Page description, added in a meta tag
   */
  description: string;

  /**
   * An optional back button props
   * Default behaviour is to show the back button that navigates to the dashboard
   */
  backButton?: IPageWrapperBackButtonProps;

  /**
   * An optional boolean prop to show the {@link ScrollUpButton}.
   * If provided, the button will be shown only when the page has detectable overflow.
   * Recommended to also provide `isLoading` prop so that the overflow can be calculated correctly.
   */
  showScrollUpButton?: boolean;

  /**
   * A page heading string which will be rendered on top of the contents
   * If a string is provided, it will be rendered inside the Heading1 component.
   * If a React node is provided, it will be rendered as is & will replace the Heading1
   * and the BackButton inside the PageHeading component.
   */
  heading: string | React.ReactNode;

  /**
   * An optional boolean prop to override whether to show the heading in DOM. If false, the heading will just be added to a visually hidden
   * element for accessibility purposes
   * Default behaviour is to show the heading only in mobile layout
   */
  showHeading?: boolean;

  /**
   * Panes to be rendered in the page. If only one pane is passed, it will be rendered in full width
   */
  panes:
    | {
        /**
         * Left pane
         */
        left: React.ReactNode;
        /**
         * Right pane
         */
        right: React.ReactNode;
        /**
         * Optional width percentage of the left pane. Only applicable when the page is rendered in horizontal layout
         *
         * @default 0.413
         */
        leftPerc?: number;
      }
    | {
        /**
         * Single pane
         */
        singlePane: React.ReactNode;
      };

  /**
   * Optional prop to specify avatars to be rendered
   */
  avatars?: IAvatarButtonRowProps | false | null | undefined;

  /**
   * Optional prop to specify the maximum {@link ScreenSize} at which the page should be rendered in mobile layout
   *
   * @default 'medium'
   */
  mobileBreakpoint?: ScreenSize;

  /**
   * Optional prop to pass up loading state of the page.
   * The {@link ScrollUpButton} relies on checking for overflow after page content is loaded.
   *
   * @default false
   */
  isLoading?: boolean;

  /**
   * Allow for customizing the position of the page heading.
   * If you set this, it will override logic in this component which places
   * the page heading depending on screen size to create a "sticky" left column.
   */
  pageHeadingPosition?: 'top' | 'left';

  /**
   * Whether sticky scrolling should be used to scroll the left and right panes separately.
   * Defaults to true (with sticky scrolling on screens that are tall enough), false will scroll the entire page (normal scrolling).
   */
  useStickyScrolling?: boolean;

  /**
   * Custom max width in pixels
   */
  maxWidthOverride?: number;

  /**
   * Custom padding override (as a string)
   */
  paddingOverride?: string;

  /**
   * Additional classnames for subcomponents
   */
  classNames?: {
    pageWrapper?: string;
  };
}

interface IPageWrapperBackButtonProps {
  /**
   * An optional boolean prop to show the back button
   *
   * @default true
   */
  show?: boolean;

  /**
   * An optional href for the back button. If not provided, the back button will navigate to the dashboard
   */
  href?: string;

  /**
   * Whether to replace history instead of pushing a new entry
   *
   * @default false
   */
  replaceHistory?: boolean;

  /**
   * Whether to use the webview navigation for the back button. If false, the back button will use web navigation.
   */
  useWebViewNavigation?: boolean;
}

interface IBackButtonProps {
  pageName: string;
  isCondensed: boolean;
  href?: string;
  replaceHistory?: boolean;
  useWebViewNavigation?: boolean;
}

interface IPageHeadingProps {
  pageName: string;
  /**
   * If a string is provided, it will be rendered inside the Heading1 component.
   * If a React node is provided, it will be rendered as is & will replace the Heading1
   * and the BackButton inside the PageHeading component.
   */
  heading: string | React.ReactNode;
  isCondensed: boolean;
  backButton?: IPageWrapperBackButtonProps;
  showHeading: boolean;
}

export const BackButton = (props: IBackButtonProps) => {
  const { pageName, isCondensed, href = dashboardPath, replaceHistory = false, useWebViewNavigation } = props;
  const { t } = useLocalizedStrings();
  const className = getBackButtonClassName(isCondensed);
  const backIcon = <BackIcon />;
  const history = useHistory();
  const { navigateTo } = useNavigation();
  const isWebView = pageDataService.isWebView();

  // For accessibility, always render webview back button as actual button element
  // instead of an anchor element.
  if (isWebView) {
    return (
      <PillButton
        className={className}
        onClick={() => (replaceHistory ? history.replace(href) : navigateTo(href, true))}
        useWebViewNavigation={true}
        text={isCondensed ? undefined : t('Back')}
        aria-label={t('BackAriaLabel')}
        onRenderIcon={() => backIcon}
        telemetryEventName={`${pageName}Back`}
      />
    );
  }

  return (
    <PillButton
      className={className}
      href={replaceHistory ? undefined : href}
      onClick={replaceHistory ? () => history.replace(href) : undefined}
      useWebViewNavigation={useWebViewNavigation}
      text={isCondensed ? undefined : t('Back')}
      aria-label={t('BackAriaLabel')}
      onRenderIcon={() => backIcon}
      telemetryEventName={`${pageName}Back`}
    />
  );
};

const PageHeading = (props: IPageHeadingProps) => {
  const { pageName, heading, isCondensed, backButton = { show: true }, showHeading } = props;
  const className = getPageHeadingClassName(isCondensed);

  const isString = typeof heading === 'string';

  return (
    <div
      className={isString ? className : undefined}
      style={
        backButton.show || showHeading
          ? undefined
          : // if we are are showing neither back button nor heading, then render this div just for screen reader to avoid extra padding on top of page (added by flex gap)
            screenReaderOnlyStyles
      }
    >
      {isString ? (
        <>
          {backButton.show && (
            <BackButton
              pageName={pageName}
              isCondensed={isCondensed}
              href={backButton.href}
              replaceHistory={backButton.replaceHistory}
              useWebViewNavigation={backButton.useWebViewNavigation}
            />
          )}
          <Heading1 block variant={isCondensed ? 'Subtitle2' : 'xxLarge'} style={showHeading ? undefined : screenReaderOnlyStyles}>
            {heading}
          </Heading1>
        </>
      ) : (
        heading
      )}
    </div>
  );
};

/**
 * Page wrapper is a common reponsive layout component that renders
 * - page title
 * - page description
 * - page heading
 * - back button
 * - avatar row
 * - one/two panes (vertical or horizontal depending on screen size) for content
 * - scroll up button
 *
 * Sandbox demo at `/sandbox/page_wrapper`
 *
 * @param props
 */
export const PageWrapper = (props: IPageWrapperProps) => {
  const {
    pageName,
    title,
    description,
    backButton,
    showHeading,
    showScrollUpButton = false,
    heading,
    panes,
    avatars,
    mobileBreakpoint = ScreenSize.medium,
    isLoading = false,
    pageHeadingPosition: pageHeadingPositionOverride,
    useStickyScrolling = true,
    maxWidthOverride,
    paddingOverride,
    classNames: customClassNames,
  } = props;
  const { screenSize, isShortScreen } = useScreen();

  const [hasOverflow, setHasOverflow] = useState(false);

  const isVertical = screenSize <= mobileBreakpoint;
  const isOnePane = 'singlePane' in panes;
  const leftPanePerc = isOnePane ? 1 : panes.leftPerc ?? 0.413;

  const [rightPanePaddingRef, { offsetHeight: rightPanePadding }] = useElementSize();

  const classNames = getClassNames(isVertical, leftPanePerc, rightPanePadding, screenSize, maxWidthOverride, paddingOverride);

  const avatarRow = useMemo(() => avatars && avatars.people.length > 0 && <AvatarButtonRow {...avatars} />, [avatars]);

  useOnEvent(document.getElementById(layout_wrapper_id), 'scroll', ({ target }) => {
    if (showScrollUpButton && target) {
      setHasOverflow((target as HTMLElement).scrollTop > 0); // show scroll up button only when scroll position is not at top
    }
  });
  const scrollUpButton = hasOverflow && !isLoading ? <ScrollUpButton /> : undefined;

  const pageHeadingPosition = pageHeadingPositionOverride ?? (isVertical || isOnePane ? 'top' : 'left');

  useBreakOnFirstRender();

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <Stack className={clsx(classNames.pageWrapper, customClassNames?.pageWrapper)}>
        {pageHeadingPosition === 'top' && heading && (
          <PageHeading
            pageName={pageName}
            heading={heading}
            isCondensed={isVertical}
            backButton={backButton}
            showHeading={showHeading ?? isVertical}
          />
        )}
        {isOnePane ? (
          <>
            <Stack.Item>{avatarRow}</Stack.Item>
            {panes.singlePane}
          </>
        ) : (
          <Stack horizontal={!isVertical} tokens={tokens.twoPaneContainer} grow>
            {/* left/top pane */}
            <Stack.Item className={classNames.leftPaneContainer}>
              <Stack.Item className={isShortScreen || !useStickyScrolling ? undefined : classNames.leftPaneSticky}>
                <div
                  ref={
                    // this is used to capture the height of the elements above panes.left
                    // so that we can add top padding above panes.right to align the two panes
                    rightPanePaddingRef
                  }
                >
                  {pageHeadingPosition === 'left' && (
                    // render PageHeading in left pane (instead of being at top of page) so that it is sticky as well
                    <PageHeading
                      pageName={pageName}
                      heading={heading}
                      isCondensed={isVertical}
                      backButton={backButton}
                      showHeading={showHeading ?? isVertical}
                    />
                  )}
                  {avatarRow}
                </div>
                {panes.left}
              </Stack.Item>
            </Stack.Item>

            {/* right/bottom pane */}
            <Stack.Item className={classNames.rightPaneContainer}>{panes.right}</Stack.Item>
          </Stack>
        )}
      </Stack>
      {scrollUpButton}
    </>
  );
};
